import { Button, Popover, PopoverContent, PopoverTrigger } from '@invent/wl-ui-kit-next/ui';
import { Keyboard } from 'lucide-react';
import * as React from 'react';

/**
 * What a template's fields answer to, behind the keyboard button at the right of Content — or
 * Ctrl+/ from anywhere on the page: every command and shortcut the editor keeps, the one place they
 * are described. Each line is one the editor answers; one it does not is not listed. Keys are
 * written Ctrl, as the rest of the portal writes them.
 */

/** A key as the portal draws one: the search box's Ctrl K, the insert list's Ctrl Space. */
function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border border-border bg-white px-1.5 font-sans text-[0.6875rem] leading-[1.1rem] font-normal whitespace-nowrap text-muted-foreground">
      {children}
    </kbd>
  );
}

/** One key or combination per chip; `or` between alternatives; a gesture as words. */
type Stroke = string | { words: string };

function Keys({ keys }: { keys: readonly Stroke[] }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {keys.map((key, at) =>
        typeof key === 'string' && key !== 'or' ? (
          <Key key={at}>{key}</Key>
        ) : (
          <span key={at} className="text-muted-foreground">
            {typeof key === 'string' ? key : key.words}
          </span>
        )
      )}
    </span>
  );
}

const SECTIONS: readonly { title: string; rows: readonly (readonly [readonly Stroke[], React.ReactNode])[] }[] = [
  {
    title: 'Insert',
    rows: [
      [['{{'], 'A placeholder, in any field'],
      [['/'], 'In the body: a block, a portal block, a placeholder or a condition; in a line, a placeholder'],
      [['Ctrl Space'], 'All placeholders, on what you typed'],
      [['Shift', 'or', 'Ctrl', { words: '+ click' }], 'In All placeholders: put one in and keep the list open'],
      [['↑', '↓'], 'Move through the list'],
      [['Enter', 'or', 'Tab'], 'Put in the one marked'],
      [['Esc'], 'Close the list, what you typed kept']
    ]
  },
  {
    title: 'Format — the body',
    rows: [
      [['Ctrl B', 'Ctrl I', 'Ctrl U'], 'Bold, italic, underline'],
      [[{ words: 'Select words' }], 'The toolbar over them: bold, italic, underline, a link, lists'],
      [['#', '##', '###'], 'Then a space: a heading, a subheading, a smaller heading'],
      [['-', 'or', '*'], 'Then a space: a bulleted list'],
      [['1.'], 'Then a space: a numbered list'],
      [['---'], 'A divider'],
      [['Shift Enter'], 'A line break inside the paragraph'],
      [['Ctrl Z', 'Ctrl Shift Z'], 'Undo, redo']
    ]
  },
  {
    title: 'Blocks and pills',
    rows: [
      [[{ words: 'Click' }, 'or', 'Enter'], 'On a placeholder pill, or on one selected: its fallback'],
      [['Enter'], 'On a selected button, image, section…: its settings'],
      [[{ words: 'The handle' }], 'At a block’s left: show it always or only if a condition holds, turn it into another, move it, delete it'],
      [['Esc'], 'Close a fallback or a block’s settings']
    ]
  },
  {
    title: 'Saving',
    rows: [[['Ctrl S'], 'Save the draft']]
  }
];

/** The keyboard button at the right of Content, and Ctrl+/ (⌘+/) toggling its list anywhere. */
export function CommandsButton() {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.key !== '/') return;
      event.preventDefault();
      setOpen((was) => !was);
    };
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="size-7 p-0 text-muted-foreground"
          aria-label="Commands and shortcuts (Ctrl+/)"
          title="Commands and shortcuts (Ctrl+/)"
          data-commands-button=""
        >
          <Keyboard className="size-4" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-[min(36rem,80dvh)] w-[min(34rem,calc(100vw-2rem))] overflow-y-auto p-4 text-[0.8125rem]"
        aria-label="Commands and shortcuts"
        data-commands=""
      >
        <h3 className="mb-2 text-[0.9375rem] font-semibold text-[var(--color-royal-blue)]">Commands and shortcuts</h3>
        <div className="grid gap-3">
          {SECTIONS.map((section) => (
            <section key={section.title} aria-label={section.title}>
              <h4 className="mb-1 text-xs font-medium text-muted-foreground">{section.title}</h4>
              <dl className="grid grid-cols-[minmax(7.5rem,auto)_1fr] items-baseline gap-x-4 gap-y-1">
                {section.rows.map(([keys, what], at) => (
                  <React.Fragment key={at}>
                    <dt>
                      <Keys keys={keys} />
                    </dt>
                    <dd>{what}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
